import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '../common/autocomplete';
import constants from '../../containers/function/constants';
import MultipleTagsInput from '../common/inputs/multipleTagsInput';
import strings from '../locales/';
import DateFilter from '../common/dateFilter';
import moment from 'moment';

const Header = (props) => {
  let countClickInSelectPeriodReport = 0;
  const [showDateRange, setShowDateRange] = useState(false);
  const [dateRangeText, setDateRangeText] = useState('');
  const [dateStart, setDateStart] = useState('');
  const [dateEnd, setDateEnd] = useState('');

  /**
   * initialize component state the first time it is drawn
   */
  useEffect(() => {
    initState();

    return () => {}
  }, []);

  /**
   * initialize component states
   */
  const initState = () => {
    const { filters, defaultRangeInDay } = props;
    const filterDateStart = filters.date.fechaStartFinal ? getFormatDate(filters.date.fechaStartFinal, false) : getDefaultDate(-defaultRangeInDay);
    const filterDateEnd = filters.date.fechaEndFinal ? getFormatDate(filters.date.fechaEndFinal, false) : getDefaultDate(0);

    setDateStart(filterDateStart);
    setDateEnd(filterDateEnd);

    // if the last selected filter was "Customize", get the text with the selected date range
    if (props.filters.periodReport === constants.periodReportTypes.customize) {
      getDateRangeText(filterDateStart, filterDateEnd);
    }
  }

  /**
   * returns date depending on the parameters sent
   * @param {String} filter date selected in the filter
   * @param {String} date default date in case you don't have a filter
   * @returns date according to the sent parameters
   */
  const validateDate = (filter, date) => {
    let dateValue;
    if (filter) {
      dateValue = filter;
    } else if (date) {
      dateValue = date.substring(0, 10);
    } else {
      dateValue = new Date();
    }
    return dateValue;
  };

  /**
   * formats a date with the format associated with the user's account
   * @param {String} date date to format
   * @param {Boolean} useFormatISO determines if the ISO format YYYY-MM-DD should be used
   * @returns date with the format associated with the account
   */
  const getFormatDate = (date, useFormatISO) => {
    const { language } = props;
    const format = useFormatISO ? 'YYYY-MM-DD' : language.account.date.format;
    return moment(date).format(format);
  }

  /**
   * gets a default date with days off requested
   * @param {Number} diffDays number of days apart
   * @returns date with the days of difference
   */
  const getDefaultDate = (diffDays) => {
    const defaultDate = moment().add(diffDays, 'days');
    return getFormatDate(defaultDate, false);
  }

  /**
   * sends the selected filters to the parent component
   * @param {Object} ev event of the control that executes this method
   */
  const handleSubmitFilterReport = (ev) => {
    const filterValue = ev.target.value;

    if (filterValue === constants.periodReportTypes.customize) {
      setShowDateRange(true);
    } else {
      props.handleSubmit(ev);
      countClickInSelectPeriodReport = 0;
    }
  }

  /**
   * handles the click event of the select component when the "Customize" item is selected
   * @param {Object} ev event of the control that executes this method
   */
  const handleOnClickFilterReport = (ev) => {
    const filterValue = ev.target.value;
    countClickInSelectPeriodReport++;

    // if the second click is done on the select component and select the "Customize" item, it opens the popup of the date range
    if (countClickInSelectPeriodReport == 2 && filterValue === constants.periodReportTypes.customize) {
      setShowDateRange(true);
    }

    // if the second click was made, return the counter to zero to re-handle the click event
    if (countClickInSelectPeriodReport == 2) {
      countClickInSelectPeriodReport = 0;
    }
  }

  /**
   * close the date range popup
   */
  const handleCloseDateRange = () => {
    document.getElementById('period-report').value = props.filters.periodReport;
    setShowDateRange(false);
  };

  /**
   * sets the date in the component state relative to the selected datepicker
   * @param {String} option determines which input was selected when the datepicker was opened
   * @param {Object} e object generated by the datepicker
   */
  const handleSetDate = (option, e) => {
    switch (option) {
      case 'date-start':
        setDateStart(e.date);
        break;
      case 'date-end':
        setDateEnd(e.date);
        break;
    }
  }

  /**
   * send the date filters to the parent component and close the date range popup
   */
  const handleCloseDateRangeAndSubmit = () => {
    const obj = {
      target: {
        id: 'period-report',
        value: constants.periodReportTypes.customize,
        date: {
          start: dateStart,
          end: dateEnd
        }
      }
    };
    
    props.handleSubmit(obj);
    setShowDateRange(false);
    getDateRangeText(dateStart, dateEnd);
    countClickInSelectPeriodReport = 0;
  }

  /**
   * get text with selected dates
   * @param {String} dateStart start date
   * @param {String} dateEnd end date
   */
  const getDateRangeText = (dateStart, dateEnd) => {
    const text = `: ${dateStart} ${props.language.summary.nfa.to} ${dateEnd}`
    setDateRangeText(text);
  }

  return (!props.loadingFilter && !props.loadingWorkingShiftGroups) ? (
    <div className="header">
      <MultipleTagsInput
        categories={props.filter}
        filteredTags={props.filters.tags}
        language={props.language.filter}
        handleSubmit={props.handleSubmit}
      />
      <div className="box-form islabel box170">
        <div>
          <label>{props.language.filter.workShiftGroup}</label>
        </div>
        <div>
          <select
            id="working-shift-group"
            defaultValue={props.filters.workingShiftGroup ? props.filters.workingShiftGroup : false}
            onChange={props.handleSubmit}
          >
            <option value="">
              {props.language.tasks_manager.filters['all'].toUpperCase()}
            </option>
            {props.filterWorkingShiftGroups.map((group, i) => (
              <option
                value={group.key}
                key={`group-${i}`}
              >
                {group.value}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="box-form islabel box170">
        <div>
          <label>{props.language.filter.person}</label>
        </div>
        <div>
          <Autocomplete
            value={props.filters.driver ? props.filters.driver.alias : strings.all}
            save={strings.all}
            id={`driver`}
            alias="alias"
            select="id"
            idItem="id"
            filter={props.drivers ? props.drivers : []}
            handleSubmit={props.handleSubmit}
          />
        </div>
      </div>
      <div className="box-form islabel box170">
        {/* report filter title */}
        <div>
          <label>{props.language.filter.report}</label>
        </div>

        {/* popup date range filter */}
        <div className="multiple-tags-input">
          {
            showDateRange && (
              <div onClick={handleCloseDateRange} className="multiple-tags-input-popup-glass-panel"/>
            )
          }
          <div className={`multiple-tags-input-popup-wrapper small-wrapper date-range-position ${showDateRange ? "" : "hide-popup"}`}>
            <div className="multiple-tags-input-popup-inner-wrapper">
              <div className="multiple-tags-input-popup-inputs-container">
                <div className="multiple-tags-input-popup-inputs-container-slice flex flex-direction-row">
                  <div className="box-form islabel box170">
                    <div>
                      <label>{props.language.filter.from}</label>
                      <DateFilter
                        date={validateDate(props.filters.date.fechaStartFinal, null)}
                        dif={props.filters.date.fechaStartFinal ? 0 : -(props.defaultRangeInDay*24*60*60*1000)}
                        id="date-start"
                        handleSubmit={(e) => handleSetDate('date-start', e)}
                      />
                    </div>
                    <div />
                  </div>
                  <div className="box-form islabel box170">
                    <div>
                      <label>{props.language.filter.to}</label>
                      <DateFilter
                        date={validateDate(props.filters.date.fechaEndFinal, null)}
                        dif={0}
                        id="date-end"
                        handleSubmit={(e) => handleSetDate('date-end', e)}
                      />
                    </div>
                    <div />
                  </div>
                </div>
              </div>
              <div className="multiple-tags-input-popup-buttons-container">
                <div className="big-button cancel-button" onClick={handleCloseDateRange}>
                  {props.language.filter.cancel}
                </div>
                <div className="big-button" onClick={handleCloseDateRangeAndSubmit}>
                  {props.language.filter.apply}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* select period report */}
        <div className="filter-report-container">
          <select
            id="period-report"
            defaultValue={props.filters.periodReport ? props.filters.periodReport : false}
            onChange={(e) => handleSubmitFilterReport(e)}
            onClick={(e) => handleOnClickFilterReport(e)}
          >
            <option value={constants.periodReportTypes.lastDataReport}>
              {props.language.filter.lastDataReportSleep}
            </option>
            <option value={constants.periodReportTypes.lastWeekReport}>
              {props.language.filter.lastWeekReport}
            </option>
            <option value={constants.periodReportTypes.lastMonthReport}>
              {props.language.filter.lastMonthReport}
            </option>
            <option value={constants.periodReportTypes.customize}>
              {`${props.language.filter.customize}${dateRangeText}`}
            </option>
          </select>
        </div>
      </div>
    </div>
  ) : (
    <div className="header" />
  )
};

Header.propTypes = {
  filter: PropTypes.oneOfType([PropTypes.string, PropTypes.array, PropTypes.object]).isRequired,
  language: PropTypes.oneOfType([PropTypes.object]).isRequired
};

export default Header;



// WEBPACK FOOTER //
// ./source/client/components/last_data_ffd_sleep/header.jsx